import React, { useState, useEffect } from "react"
import { getSrc } from "gatsby-plugin-image"
import { useStaticQuery, graphql, navigate, Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"

import Collapsible from "react-collapsible"
import parse from "html-react-parser"
import { parseLink } from "@utils/urlParser"

import { ImageSet } from "@components/common/image-set/ImageSet"

import TennentsLogoFull from "@assets/images/tennents-logo-full.png"
import TennentsLogoFull2x from "@assets/images/tennents-logo-full@2x.png"

import ArrowRight from "@assets/svg/arrowRight.inline.svg"
import Plus from "@assets/icons/plus.inline.svg"
import Minus from "@assets/icons/minus.inline.svg"

import Twitter from "@assets/svg/twitter.inline.svg"
import Facebook from "@assets/svg/facebook.inline.svg"
import Instagram from "@assets/svg/instagram.inline.svg"
import Tripadvisor from "@assets/svg/tripadvisor.inline.svg"
import Youtube from "@assets/svg/youtube.inline.svg"

import * as s from "./Footer.module.scss"

const Footer = () => {
  const [isClient, setClient] = useState(false)
  const key = isClient ? "client" : "server"

  useEffect(() => {
    setClient(true)
  }, [])

  const { wpPage, mainMenu, socialMenu, footerBarMenu } = useStaticQuery(
    graphql`
      query FooterQuery {
        footerBarMenu: allWpMenuItem(
          filter: { locations: { eq: FOOTERBARMENU } }
        ) {
          nodes {
            url
            title: label
            connectedNode {
              node {
                uri
              }
            }
          }
        }

        socialMenu: allWpMenuItem(filter: { locations: { eq: SOCIALMENU } }) {
          nodes {
            url
            title: label
          }
        }

        mainMenu: allWpMenuItem(filter: { locations: { eq: HEADER_MENU } }) {
          nodes {
            order
            url
            title: label
            childItems {
              nodes {
                order
                url
                title: label
              }
            }
          }
        }

        wpPage: wpNodeWithTemplate(template: { templateName: { eq: "Home" } }) {
          ... on WpPage {
            acfHome {
              footerMenuImage {
                image {
                  altText
                  sourceUrl
                  localFile {
                    childImageSharp {
                      gatsbyImageData(
                        placeholder: DOMINANT_COLOR
                        quality: 80
                        formats: WEBP
                        width: 171
                        height: 38
                      )
                    }
                  }
                }
                link {
                  target
                  url
                  title
                }
              }
              footerCtas {
                color
                image {
                  localFile {
                    childImageSharp {
                      gatsbyImageData(
                        placeholder: BLURRED
                        quality: 80
                        formats: [WEBP]
                      )
                    }
                  }
                }
                link {
                  target
                  title
                  url
                }
                title
                subTitle
              }
            }
          }
        }
      }
    `,
  )

  const getSocialIcon = (name: string) => {
    switch (name) {
      case "Twitter":
        return <Twitter />
        break
      case "Facebook":
        return <Facebook />
        break
      case "Instagram":
        return <Instagram />
        break
      case "Trip Advisor":
        return <Tripadvisor />
        break
      case "Youtube":
        return <Youtube />
        break

      default:
        return <Twitter />
    }
  }

  if (!isClient) return null

  return (
    <div className={s.footer} key={key}>
      <div className={s.footer__main}>
        <div className={s.footer__main___top}>
          <div className={s.footer__main___topLogo}>
            <div>
              <ImageSet
                oneX={TennentsLogoFull}
                twoX={TennentsLogoFull2x}
                alt={"Tennent's Est. 1885"}
                width={142}
                height={62}
              />
            </div>
          </div>
          <div className={s.footer__main___topMenu}>
            {socialMenu.nodes.map((item, idx) => {
              return (
                <React.Fragment key={idx}>
                  <a href={item.url} className={s.footer__main___topMenuItem} aria-label={`follow us on ${item.title}`}>
                    {item && getSocialIcon(item.title)}
                  </a>
                </React.Fragment>
              )
            })}
          </div>
        </div>

        <div className={s.footer__main___bottom}>
          {mainMenu.nodes
            .filter(items => items.childItems.nodes.length > 0)
            .map((items, idx) => {
              return (
                <div key={idx}>
                  <div className={s.footer__main___bottomDesktopMenus}>
                    <Link to={parseLink(items.url)}>
                      <h5 className={s.footer__main___bottomMenusTitle}>
                        {items.title}
                      </h5>
                    </Link>

                    <ul>
                      {items.childItems.nodes.length > 0 &&
                        items.childItems.nodes.map((item, i) => {
                          return (
                            <li key={i}>
                              <Link to={parseLink(item.url)}>
                                {item && parse(item.title)}
                              </Link>
                            </li>
                          )
                        })}
                    </ul>
                  </div>
                  <Collapsible
                    key={idx}
                    className={s.footer__main___bottomMenus}
                    triggerWhenOpen={
                      <div className={s.footer__main___bottomTrigger}>
                        <Link to={items.url}>
                          <h5 className={s.footer__main___bottomMenusTitle}>
                            {parse(items.title)}
                          </h5>
                        </Link>
                        <Minus />
                      </div>
                    }
                    trigger={
                      <div className={s.footer__main___bottomTrigger}>
                        <h5 className={s.footer__main___bottomMenusTitle}>
                          {parse(items.title)}
                        </h5>

                        <Plus />
                      </div>
                    }
                  >
                    <ul>
                      {items.childItems.nodes.length > 0 &&
                        items.childItems.nodes.map((child, i) => (
                          <li key={i}>
                            <Link to={parseLink(child.url)}>
                              {parse(child.title)}
                            </Link>
                          </li>
                        ))}
                    </ul>
                  </Collapsible>
                </div>
              )
            })}
        </div>

        <div className={s.footer__main___bar}>
          {wpPage.acfHome.footerMenuImage.image && (
            <div className={s.footer__main___barLogos}>
              <a
                href={wpPage.acfHome.footerMenuImage.link.url}
                target="_blank"
                rel="noreferrer"
              >
                {wpPage.acfHome.footerMenuImage.image.localFile ? (
                  <GatsbyImage
                    alt={wpPage.acfHome.footerMenuImage.image.altText}
                    image={
                      wpPage.acfHome.footerMenuImage.image.localFile
                        .childImageSharp.gatsbyImageData
                    }
                  />
                ) : (
                  <img
                    className={s.footer__main___barLogosdrinkAware}
                    src={wpPage.acfHome.footerMenuImage.image.sourceUrl}
                    alt={wpPage.acfHome.footerMenuImage.image.altText}
                    width={171}
                    height={38}
                  />
                )}
              </a>
            </div>
          )}

          {footerBarMenu.nodes.length > 0 && (
            <div className={s.footer__main___barNote}>
              {footerBarMenu.nodes.map((items, idx) =>
                items.connectedNode ? (
                  <Link to={items.url} key={idx}>
                    {items.title}
                  </Link>
                ) : (
                  <a
                    href={items.url}
                    key={idx}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {items.title}
                  </a>
                ),
              )}
            </div>
          )}
        </div>
      </div>
      <div className={s.footer__sidebar}>
        {wpPage.acfHome.footerCtas &&
          wpPage.acfHome.footerCtas.map((cta, idx) => {
            return cta.image ? (
              <div
                key={idx}
                className={s.footer__sidebar___cta}
                style={{
                  background: `url(${
                    cta.image && getSrc(cta.image.localFile)
                  }), ${cta.color && cta.color}`,
                }}
                onClick={() => {
                  navigate(cta.link.url.replace(/^.*\/\/[^\/]+/, ""))
                }}
              >
                <div className={s.footer__sidebar___ctaArrow}>
                  <ArrowRight />
                </div>

                <div>
                  <p>{cta.subTitle}</p>
                  <h4>{cta.title}</h4>
                </div>
              </div>
            ) : (
              <div
                key={idx}
                className={s.footer__sidebar___cta}
                style={{
                  background: `${cta.color && cta.color}`,
                }}
                onClick={() => {
                  navigate(cta.link.url.replace(/^.*\/\/[^\/]+/, ""))
                }}
              >
                <div className={s.footer__sidebar___ctaArrow}>
                  <ArrowRight />
                </div>

                <div>
                  <p>{cta.subTitle}</p>
                  <h4>{cta.title}</h4>
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default Footer
